import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { selectAuthToken } from 'selectors';
import type { AxiosResponse } from 'axios';
import { useNotification } from 'hooks/useNotification';
import { integrationQueryKeys } from './integrationQueryKeys';
import { UpdateSyncFrequencyParams, UpdateSyncFrequencyResponse } from 'IntegrationModule/types';
import { updateSyncFrequencyApi } from '../updateSyncFrequencyApi';

export const useUpdateSyncFrequencyMutation = () => {
  const token = useSelector(selectAuthToken);
  const { showSuccess, showError } = useNotification();
  const queryClient = useQueryClient();

  const mutation = useMutation<
    AxiosResponse<UpdateSyncFrequencyResponse>,
    AxiosResponse<{ error: string; message: string; statusCode: number }>,
    UpdateSyncFrequencyParams
  >({
    mutationFn: async (params) => {
      if (!token) throw new Error('Not Authenticated');
      return updateSyncFrequencyApi({ params, token }) as Promise<
        AxiosResponse<UpdateSyncFrequencyResponse>
      >;
    },
    onSuccess: (_data, variables) => {
      const { customDailyFetchCount, customDailyFetchTime } = variables;
      showSuccess(
        customDailyFetchTime?.length
          ? `Sync frequency updated to ${customDailyFetchCount}x per day (${customDailyFetchTime.join(
              ', ',
            )} UTC)`
          : `Sync frequency updated to ${customDailyFetchCount}x per day`,
      );
      queryClient.invalidateQueries({ queryKey: integrationQueryKeys.lists() });
    },
    onError: (error) => {
      showError(`Failed to update sync frequency: ${error?.data?.message || 'Unknown error'}`);
    },
  });

  return {
    updateSyncFrequency: mutation.mutate,
    updateSyncFrequencyAsync: mutation.mutateAsync,
    isUpdatingSyncFrequency: mutation.isPending,
  };
};
